import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  Link,
  SearchInput,
  Space,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  TableScrollbar,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { AddIcon, CloseSIcon } from '@tonic-one/react-icons';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { dataAttr } from '@tonic-one/utils';
import SmartTooltip from '@Report/SmartTooltip';
import { OrgApi } from '@Apis';
import { useTranslation } from 'react-i18next';
import {
  AddOrgInfo,
  OrgInfo4Table,
  OrgInfoResDto,
  UpdateOrgInfo,
} from '../utils/types';
import CSpinner from '@Components/CSpinner';
import ErrorAlert from '@Components/Common/ErrorAlert';
import AddAndEditOrgModal from './AddOrgModal';
import DelOrgModal from './DelOrgModal';
import { char2entity, joinStringArr } from '../../../../src/utils/common';
import { getServiceNamesMapping, SERVICE_TYPES } from 'src/utils/service';
import IconButton from '@Components/IconButton';
import i18next from 'i18next';
import { PermissionNameEnum } from '@Types/permissions';
import { usePermissionStore } from '@Models/usePermissionModel';
import { useOrgStore } from '../../../models/useOrgModel';
import { useAccountStore } from '@Models/useAccountModel';

const OrganizationManagement = React.memo(() => {
  const { groupCompanyGuid } = useAccountStore();
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [rowSelection, setRowSelection] = useState({});

  const [allData, setAllData] = useState<OrgInfo4Table[]>([]);
  const [keyword, setKeyword] = useState('');

  const filteredData = useMemo(() => {
    const lowerCaseKeyword = keyword.toLowerCase();
    return keyword === ''
      ? allData
      : allData.filter(({ orgName, orgDescription, orgServices }) => {
          return (
            orgName.toLowerCase().includes(lowerCaseKeyword) ||
            orgDescription.toLowerCase().includes(lowerCaseKeyword) ||
            orgServices.toLowerCase().includes(lowerCaseKeyword)
          );
        });
  }, [allData, keyword]);

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const [isDelOrgModalVisible, setIsDelOrgModalVisible] =
    React.useState<boolean>(false);
  const [isAddOrgModalVisible, setIsAddOrgModalVisible] =
    React.useState<boolean>(false);
  const [isEditOrgModalVisible, setIsEditOrgModalVisible] =
    React.useState<boolean>(false);
  const [updateOrgInfo, setUpdateOrgInfo] = useState<UpdateOrgInfo>();
  const resetDel = () => {
    setRowSelection([]);
  };
  const { widthEditablePermission, managementScopeIds } = usePermissionStore();
  const { setOrgs } = useOrgStore();

  const resetOrgModel = async () => {
    const orgs = await OrgApi.getOrgs();
    setOrgs(orgs);
  };

  const handleRemoveAction = async () => {
    setLoading(true);
    const selectedRows = getSelectedRows();
    try {
      for (const orgId of selectedRows) {
        await OrgApi.removeOrg(orgId);
      }
      resetTable();
      resetOrgModel();
    } catch (error: any) {
      const code = error?.status || 'Unknown';
      if (code === 409) {
        window.showNotification({
          titleMsg: t('administration.apiException.delOrgError'),
          severity: 'error',
        });
      } else {
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      }
    } finally {
      setIsDelOrgModalVisible(false);
      setLoading(false);
      await fetchData();
    }
  };

  function validateOrgName(orgName: string) {
    if (!orgName) {
      return {
        orgName: {
          title: t('errors.administrator.org.emptyOrgNameTitle'),
          content: t('errors.administrator.org.emptyOrgNameDesc'),
        },
      };
    } else {
      return null;
    }
  }

  const handleAddOrgModalClose = useCallback(() => {
    setIsAddOrgModalVisible(false);
    setIsEditOrgModalVisible(false);
  }, []);

  const handleOrgSave = async (addOrgInfo: AddOrgInfo) => {
    setLoading(true);
    try {
      const res = validateOrgName(addOrgInfo.orgName);
      if (res) {
        window.showNotification({
          titleMsg: res.orgName.title,
          bodyMsg: res.orgName.content,
          severity: 'error',
        });
      } else {
        await OrgApi.addOrg(addOrgInfo);
        resetOrgModel();
      }
    } catch (error: any) {
      const code = error?.status || 'Unknown';
      if (code === 409) {
        window.showNotification({
          titleMsg: t('administration.apiException.duplicateOrgError'),
          severity: 'error',
        });
      } else if (code === 400) {
        window.showNotification({
          titleMsg: t('errors.administrator.org.orgCountMaxlength', {
            NUMBER: allData.length,
          }),
          severity: 'error',
        });
      } else {
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      }
    } finally {
      handleAddOrgModalClose();
      await fetchData();
      setLoading(false);
    }
  };

  const handleOrgUpdate = async (updateOrgInfo: UpdateOrgInfo) => {
    setLoading(true);
    try {
      const res = validateOrgName(updateOrgInfo.orgName);
      if (res) {
        window.showNotification({
          titleMsg: res.orgName.title,
          bodyMsg: res.orgName.content,
          severity: 'error',
        });
      } else {
        await OrgApi.updateOrg(updateOrgInfo);
        resetOrgModel();
      }
    } catch (error: any) {
      const code = error?.status || 'Unknown';
      if (code === 409) {
        window.showNotification({
          titleMsg: t('administration.apiException.duplicateOrgError'),
          severity: 'error',
        });
      } else {
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      }
    } finally {
      handleAddOrgModalClose();
      await fetchData();
      setLoading(false);
    }
  };

  const resetTable = () => {
    setRowSelection({});
  };

  function getSelectedRows() {
    const selectedRows: string[] = [];
    for (const key of Object.keys(rowSelection)) {
      selectedRows.push(key);
    }
    return selectedRows;
  }

  const fetchData = useCallback(async () => {
    function transServiceName(orgServices: number[]) {
      if (orgServices.length === 0) {
        return '';
      }
      const provisionedServiceNames = getServiceNamesMapping(
        SERVICE_TYPES,
        orgServices
      );
      return joinStringArr(
        provisionedServiceNames.map((v) => t(`common.service.${v}`)),
        i18next.language
      );
    }
    try {
      setLoading(true);
      const allOrgsInfo: OrgInfoResDto[] = (await OrgApi.getOrgsInfo()).data;
      const allOrgsInfo4Table: OrgInfo4Table[] = allOrgsInfo.map((orgInfo) => {
        return {
          ...orgInfo,
          orgServices: transServiceName(orgInfo.orgServices || []),
        };
      });
      setAllData(allOrgsInfo4Table);
    } catch (error) {
      console.error(error);
      setErrorMsg(t('common.apiException.internalError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // org columns
  const columns: ColumnDef<OrgInfo4Table>[] = useMemo(
    () => [
      {
        id: 'selection',
        cell: ({ row }) =>
          row.original.companyGuid !== groupCompanyGuid && (
            <Flex ml="2px" alignItems="center">
              <Checkbox
                checked={row.getIsSelected()}
                disabled={!row.getCanSelect()}
                onChange={row.getToggleSelectedHandler()}
              />
            </Flex>
          ),
        meta: {
          customColumnStyle: {
            minWidth: 40,
            width: '5%',
          },
        },
      },
      {
        id: 'orgName',
        accessorKey: 'orgName',
        header: () => t('common.label.orgName'),
        cell: ({ row }) => {
          const { orgName, orgDescription, companyGuid } = row.original;
          return (
            <Link
              width="100%"
              onClick={() => {
                setUpdateOrgInfo({
                  orgName: char2entity(orgName),
                  orgDescription,
                  orgId: companyGuid,
                });
                setIsEditOrgModalVisible(true);
              }}
            >
              <SmartTooltip>{char2entity(orgName)}</SmartTooltip>
            </Link>
          );
        },
        meta: {
          customColumnStyle: {
            minWidth: 40,
            // width: '228px',
            width: '24%',
          },
        },
      },
      {
        id: 'orgDescription',
        accessorKey: 'orgDescription',
        header: () => t('common.label.orgDesc'),
        meta: {
          customColumnStyle: {
            minWidth: 40,
            // width: '285px',
            width: '32%',
          },
        },
      },
      {
        id: 'orgServices',
        accessorKey: 'orgServices',
        header: () => t('common.label.orgServices'),
        meta: {
          customColumnStyle: {
            minWidth: 40,
            // width: '380px',
            width: '38%',
          },
        },
      },
    ],
    [groupCompanyGuid, t]
  );

  // org table
  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      rowSelection,
    },
    defaultColumn: {
      minSize: 40,
    },
    getRowId: (row) => `${row.companyGuid}`,
    enableRowSelection:
      managementScopeIds === 'all' &&
      widthEditablePermission(PermissionNameEnum.administrator), // enable row selection for all rows
    // enableRowSelection: (row) => row.original.detections > 0, // or enable row selection conditionally per row
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    (async function doCall() {
      await fetchData();
    })();
  }, [fetchData]);

  return (
    <>
      <Flex
        direction="column"
        overflow="hide"
        pl="16px"
        width="100%"
        height="100%"
        display="flex"
        gap="16px"
      >
        {loading && <CSpinner />}
        {errorMsg && (
          <ErrorAlert
            errorMsg={errorMsg}
            onClose={() => setErrorMsg(undefined)}
          />
        )}
        <Text fontWeight="400" fontSize="18px" lineHeight="24px" pt="16px">
          {t('administration.org.title')}
        </Text>
        <Text color={colorStyle.color.secondary} lineHeight="lg">
          {t('administration.org.desc')}
        </Text>
        <Flex direction="row">
          {Object.keys(rowSelection).length === 0 ? (
            <>
              {managementScopeIds === 'all' && (
                <Button
                  variant="primary"
                  onClick={() => setIsAddOrgModalVisible(!isAddOrgModalVisible)}
                  ml="16px"
                  disabled={
                    !widthEditablePermission(PermissionNameEnum.administrator)
                  }
                >
                  <Icon as={AddIcon} fontWeight="bold"></Icon>
                  <Space width="2x" />
                  {t('common.action.label.add')}
                </Button>
              )}
            </>
          ) : (
            <Flex direction="row" ml="16px">
              <Button
                variant="secondary"
                disabled={
                  !widthEditablePermission(PermissionNameEnum.administrator)
                }
                onClick={() => setIsDelOrgModalVisible(true)}
              >
                {t('common.action.label.remove')}
              </Button>
              <IconButton ml="12px" onClick={resetDel}>
                <Icon as={CloseSIcon}></Icon>
              </IconButton>
              <Box lineHeight="2rem">
                <Text color={colorStyle.color.tertiary}>
                  {Object.keys(rowSelection).length}&nbsp;
                  {t('reportCommon.placeholders.selected')}
                </Text>
              </Box>
            </Flex>
          )}
          <Space width="12px" />
          <SearchInput
            placeholder={t('administration.org.search.placeholder')}
            onChange={(e: any) => {
              setKeyword(e.target.value);
            }}
            onClearInput={() => {
              setKeyword('');
            }}
            width="405px"
          />
        </Flex>
        <Table mt="16px" size="lg" display="flex">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableHeaderRow key={headerGroup.id} width="100%">
                {headerGroup.headers.map((header) => {
                  const styleProps = {
                    minWidth: header.column.columnDef.minSize,
                    width: header.getSize(),
                    ...header.column.columnDef.meta?.customColumnStyle,
                  };
                  return (
                    <TableHeaderCell key={header.id} {...styleProps}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHeaderCell>
                  );
                })}
              </TableHeaderRow>
            ))}
          </TableHeader>
          <TableScrollbar
            maxHeight="440px"
            overflow="visible" // Make the scrollbar visible
            overflowX="hidden"
          >
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-selected={dataAttr(row.getIsSelected())}
                  _hover={{
                    backgroundColor: colorStyle.background.selected,
                  }}
                  width="100%"
                >
                  {row.getVisibleCells().map((cell) => {
                    const styleProps = {
                      minWidth: cell.column.columnDef.minSize,
                      width: cell.column.getSize(),
                      ...cell.column.columnDef.meta?.customColumnStyle,
                    };
                    return (
                      <TableCell key={cell.id} {...styleProps}>
                        {cell.column.columnDef.id === 'orgName' ? (
                          flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )
                        ) : (
                          <SmartTooltip>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </SmartTooltip>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </TableScrollbar>
        </Table>
        {isAddOrgModalVisible && (
          <AddAndEditOrgModal
            title={t('administration.addOrg.title')}
            isVisible={isAddOrgModalVisible}
            onClose={handleAddOrgModalClose}
            onSaveNew={handleOrgSave}
            onUpdate={handleOrgUpdate}
          ></AddAndEditOrgModal>
        )}
        {isEditOrgModalVisible && (
          <AddAndEditOrgModal
            title={t('administration.org.title')}
            isVisible={isEditOrgModalVisible}
            onClose={handleAddOrgModalClose}
            onSaveNew={handleOrgSave}
            onUpdate={handleOrgUpdate}
            updateOrgInfo={updateOrgInfo}
          ></AddAndEditOrgModal>
        )}
        {isDelOrgModalVisible && (
          <DelOrgModal
            isVisible={isDelOrgModalVisible}
            onClose={() => setIsDelOrgModalVisible(false)}
            onDel={handleRemoveAction}
          ></DelOrgModal>
        )}
      </Flex>
    </>
  );
});

export default OrganizationManagement;
